<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import type {
    AdminGuestlistEntry,
    AdminGuestlistResponse,
    IdentityStatus,
    AdminPersonRecord
  } from '../../lib/types';
  import { downloadCsv, toCsv } from '../../lib/csv';
  import ChipGroup, { type ChipOption } from './ui/ChipGroup.svelte';
  import StatusBadge from './ui/StatusBadge.svelte';
  import IdentityBadge from './ui/IdentityBadge.svelte';
  import SurfaceCard from './ui/SurfaceCard.svelte';
  import GuestEditorModal, { type GuestPayload } from './GuestEditorModal.svelte';
  import GuestImportModal from './GuestImportModal.svelte';
  import InviteManager from './InviteManager.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import { deleteAdminTicket } from '../../lib/convexApi';

  export let eventId: string | null = null;
  export let data: AdminGuestlistResponse | null = null;
  export let loading = false;
  export let error: string | null = null;
  export let people: AdminPersonRecord[] = [];

  type StatusFilter = 'all' | 'issued' | 'redeemed' | 'pending' | 'invited';
  type ExtrasFilter = 'all' | 'has' | 'none';
  type IdentityFilter = 'all' | IdentityStatus;
  type SortKey = 'activity' | 'name';
  type SortDirection = 'asc' | 'desc';

  const dispatch = createEventDispatcher<{ back: void; refresh: void }>();

  const statusOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'issued', label: 'Issued' },
    { value: 'redeemed', label: 'Redeemed' },
    { value: 'pending', label: 'Pending' },
    { value: 'invited', label: 'Invited' }
  ];

  const extrasOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'has', label: 'With extras' },
    { value: 'none', label: 'No extras' }
  ];

  const identityOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'linked', label: 'Linked' },
    { value: 'email_only', label: 'Email only' },
    { value: 'name_only', label: 'Needs enrichment' }
  ];

  let statusFilter: StatusFilter = 'all';
  let extrasFilter: ExtrasFilter = 'all';
  let identityFilter: IdentityFilter = 'all';
  let searchTerm = '';
  let selectedTicketIds = new Set<string>();
  let selectedVisibleTicketIds = new Set<string>();
  let selectedVisibleEntries: AdminGuestlistEntry[] = [];
  let expandedTicketId: string | null = null;
  let sortKey: SortKey = 'activity';
  let sortDirection: SortDirection = 'desc';
  let showGuestModal = false;
  let editingGuest: GuestPayload | null = null;
  let deleteTarget: AdminGuestlistEntry | null = null;
  let showDeleteModal = false;
  let deleteBusy = false;
  let deleteError: string | null = null;
  let existingGuestNameKeys: string[] = [];
  let showImportModal = false;
  let importFeedback: string | null = null;
  let importFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  const statusLabels: Record<string, string> = {
    issued: 'Issued',
    redeemed: 'Redeemed',
    pending: 'Pending',
    invited: 'Invited',
    cancelled: 'Cancelled'
  };

  $: allGuests = data?.guests ?? [];
  $: filteredGuests = applyFilters(allGuests);
  $: sortedGuests = applySort(filteredGuests);
  $: visibleGuestIds = new Set(sortedGuests.map(entry => entry.ticket.id));
  $: selectedCount = Array.from(selectedTicketIds).filter(id => visibleGuestIds.has(id)).length;
  $: selectionActive = selectedCount > 0;
  $: selectedVisibleTicketIds = new Set(Array.from(selectedTicketIds).filter(id => visibleGuestIds.has(id)));
  $: selectedVisibleEntries = sortedGuests.filter(entry => selectedVisibleTicketIds.has(entry.ticket.id));
  $: existingPersonIds = Array.from(
    new Set(
      allGuests
        .map(entry => entry.person?.id)
        .filter((value): value is string => Boolean(value))
    )
  );
  $: existingGuestNameKeys = (() => {
    const keys = new Set<string>();
    for (const entry of allGuests) {
      const key = buildGuestNameKey(entry.person?.name ?? null);
      if (key) keys.add(key);
    }
    return Array.from(keys);
  })();
  $: activeEventId = eventId ?? data?.event?.id ?? null;
  $: eventQuestion = (() => {
    const value = data?.event?.ticket?.preCheckoutQuestion ?? null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })();

  function applyFilters(guests: AdminGuestlistEntry[]): AdminGuestlistEntry[] {
    const term = searchTerm.trim().toLowerCase();
    return guests.filter(entry => {
      const ticketStatus = entry.ticket.status ?? 'issued';
      if (statusFilter !== 'all' && ticketStatus !== statusFilter) return false;

      const extrasCount = entry.ticket.extras?.length ?? 0;
      if (extrasFilter === 'has' && extrasCount === 0) return false;
      if (extrasFilter === 'none' && extrasCount > 0) return false;

      if (identityFilter !== 'all' && entry.identityStatus !== identityFilter) return false;

      if (!term) return true;
      const haystacks: string[] = [];
      if (entry.person?.name) haystacks.push(entry.person.name.toLowerCase());
      if (entry.person?.email) haystacks.push(entry.person.email.toLowerCase());
      if (entry.replacedGuest?.name) haystacks.push(entry.replacedGuest.name.toLowerCase());
      if (entry.replacedGuest?.email) haystacks.push(entry.replacedGuest.email.toLowerCase());
      haystacks.push(entry.ticket.code.toLowerCase());
      return haystacks.some(value => value.includes(term));
    });
  }

  function applySort(guests: AdminGuestlistEntry[]): AdminGuestlistEntry[] {
    const sorted = [...guests];
    sorted.sort((a, b) => {
      if (sortKey === 'name') {
        const aName = a.person?.name?.toLowerCase() ?? '';
        const bName = b.person?.name?.toLowerCase() ?? '';
        return sortDirection === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      const aTs = timestampForSort(a);
      const bTs = timestampForSort(b);
      return sortDirection === 'asc' ? aTs - bTs : bTs - aTs;
    });
    return sorted;
  }

  function timestampForSort(entry: AdminGuestlistEntry): number {
    if (entry.lastActivity) {
      const parsed = Date.parse(entry.lastActivity);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDirection = key === 'name' ? 'asc' : 'desc';
    }
  }

  function toggleSelect(ticketId: string) {
    const next = new Set(selectedTicketIds);
    if (next.has(ticketId)) next.delete(ticketId);
    else next.add(ticketId);
    selectedTicketIds = next;
  }

  function selectVisible() {
    selectedTicketIds = new Set(visibleGuestIds);
  }

  function clearSelection() {
    selectedTicketIds = new Set();
  }

  function toggleExpand(ticketId: string) {
    expandedTicketId = expandedTicketId === ticketId ? null : ticketId;
  }

  function formatDateTime(value: string | null, fallback = '—'): string {
    if (!value) return fallback;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    return `${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  function formatScheduleWindow(): string {
    if (!data?.event) return '';
    const start = new Date(data.event.schedule.start);
    const end = new Date(data.event.schedule.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    return `${start.toLocaleDateString()} · ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  function extrasDisplay(entry: AdminGuestlistEntry): string[] {
    const extras = entry.ticket.extras ?? [];
    return extras.map(extra => {
      const quantity = extra.quantity ?? 0;
      const label = extra.name ?? extra.sku;
      const total = extra.totalPrice ?? null;
      const currency = extra.currency ?? data?.event?.ticket.currency ?? 'eur';
      if (total !== null) {
        try {
          return `${label} ×${quantity} · ${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase()
          }).format(total)}`;
        } catch {
          return `${label} ×${quantity} · ${currency.toUpperCase()} ${total.toFixed(2)}`;
        }
      }
      return `${label} ×${quantity}`;
    });
  }

  function normalizeStatus(status: string | null | undefined): 'issued' | 'redeemed' | 'pending' | 'invited' | 'cancelled' {
    switch (status) {
      case 'redeemed':
        return 'redeemed';
      case 'pending':
        return 'pending';
      case 'invited':
        return 'invited';
      case 'cancelled':
        return 'cancelled';
      case 'issued':
      default:
        return 'issued';
    }
  }

  function safeFilename(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '-')
      .replaceAll(/(^-|-$)/g, '');
  }

  function exportSelectedCsv() {
    if (!data?.event) return;
    if (selectedVisibleEntries.length === 0) return;

    const headers = [
      'Event',
      'Ticket code',
      'Ticket status',
      'Redeemed at',
      'Guest name',
      'Guest email',
      'Guest phone',
      'Identity status',
      'Extras',
      'Last activity',
      'Pre-checkout answer',
      'Replaced guest name',
      'Replaced guest email',
      'Replaced guest captured at'
    ];

    const rows = selectedVisibleEntries.map(entry => [
      data.event?.name ?? '',
      entry.ticket.code,
      normalizeStatus(entry.ticket.status),
      entry.ticket.redeemedAt ?? '',
      entry.person?.name ?? '',
      entry.person?.email ?? '',
      entry.person?.phone ?? '',
      entry.identityStatus,
      extrasDisplay(entry).join(' | '),
      entry.lastActivity ?? '',
      entry.ticket.preCheckoutAnswer ?? '',
      entry.replacedGuest?.name ?? '',
      entry.replacedGuest?.email ?? '',
      entry.replacedGuest?.capturedAt ?? ''
    ]);

    const csvText = toCsv(headers, rows);
    const datePart = new Date().toISOString().slice(0, 10);
    const eventPart = safeFilename(data.event?.name ?? 'event');
    downloadCsv(`${eventPart || 'event'}-guestlist-${datePart}.csv`, csvText);
  }

  function handleBack() {
    dispatch('back');
  }

  function openCreateGuest() {
    editingGuest = null;
    showGuestModal = true;
  }

  function openEditGuest(entry: AdminGuestlistEntry) {
    editingGuest = {
      ticketId: entry.ticket.id,
      name: entry.person?.name ?? '',
      email: entry.person?.email ?? null,
      personId: entry.person?.id ?? null,
      emailLocked: Boolean(entry.person?.externalId),
      redeemed: entry.ticket.status === 'redeemed'
    };
    showGuestModal = true;
  }

  function closeGuestModal() {
    showGuestModal = false;
  }

  function handleGuestSaved() {
    showGuestModal = false;
    dispatch('refresh');
  }

  function requestDelete(entry: AdminGuestlistEntry) {
    deleteTarget = entry;
    deleteError = null;
    deleteBusy = false;
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    if (deleteBusy) return;
    showDeleteModal = false;
    deleteTarget = null;
    deleteError = null;
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    deleteBusy = true;
    deleteError = null;
    try {
      await deleteAdminTicket(deleteTarget.ticket.id);
      showDeleteModal = false;
      deleteTarget = null;
      dispatch('refresh');
    } catch (error) {
      deleteError = error instanceof Error ? error.message : 'Failed to delete guest';
    } finally {
      deleteBusy = false;
    }
  }

  function buildGuestNameKey(value: string | null): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const collapsed = trimmed.replace(/\s+/g, ' ');
    try {
      return collapsed.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
    } catch (error) {
      return collapsed.toLowerCase();
    }
  }

  function openImportModal() {
    if (!activeEventId) return;
    showImportModal = true;
  }

  function closeImportModal() {
    showImportModal = false;
  }

  function handleImportComplete(event: CustomEvent<{ imported: number; skipped: number }>) {
    showImportModal = false;
    const { imported, skipped } = event.detail;
    const parts: string[] = [];
    if (imported > 0) {
      parts.push(`${imported} ${imported === 1 ? 'guest' : 'guests'} imported`);
    }
    if (skipped > 0) {
      parts.push(`${skipped} skipped as duplicates`);
    }
    importFeedback = parts.join(' · ') || 'No guests imported';
    if (importFeedbackTimer) {
      clearTimeout(importFeedbackTimer);
    }
    importFeedbackTimer = setTimeout(() => {
      importFeedback = null;
      importFeedbackTimer = null;
    }, 5000);
    dispatch('refresh');
  }

  onDestroy(() => {
    if (importFeedbackTimer) {
      clearTimeout(importFeedbackTimer);
    }
  });
</script>

<section class="guestlist">
  <header class="guestlist-header">
    <div class="breadcrumbs">
      <button type="button" class="ghost" on:click={handleBack}>← All events</button>
      <div>
        <h2>{data?.event?.name ?? 'Guestlist'}</h2>
        {#if data?.event}
          <p>{data.event.headline}</p>
        {:else if eventId}
          <p>Event {eventId}</p>
        {/if}
      </div>
    </div>

    <div class="header-stats">
      <span class="chip">{data?.ticketTotals.total ?? 0} tickets</span>
      <span class="chip">{data?.ticketTotals.issued ?? 0} open</span>
      <span class="chip">{data?.ticketTotals.redeemed ?? 0} redeemed</span>
      <span class="generated">Snapshot {formatDateTime(data ? new Date(data.generatedAt).toISOString() : null)}</span>
    </div>
  </header>

  {#if loading}
    <SurfaceCard>Loading guestlist…</SurfaceCard>
  {:else if error}
    <SurfaceCard><p class="error-text">{error}</p></SurfaceCard>
  {:else if !data}
    <SurfaceCard>Select an event to view its guestlist.</SurfaceCard>
  {:else if data.guests.length === 0}
    {#if activeEventId}
      <InviteManager eventId={activeEventId} {people} />
    {/if}
    <SurfaceCard>
      <div class="empty-state">
        <p>No guests yet for this event.</p>
        <button type="button" on:click={openCreateGuest}>Add guest</button>
      </div>
    </SurfaceCard>
  {:else}
    {#if activeEventId}
      <InviteManager eventId={activeEventId} {people} />
    {/if}
    <div class="guestlist-grid">
      <div class="main-column">
        <SurfaceCard>
        <div class="filter-stack">
          <div class="search">
            <input type="search" placeholder="Search name, email, or ticket" bind:value={searchTerm} />
          </div>
          <div class="chip-row">
              <div class="chip-block">
                <span>Status</span>
                <ChipGroup options={statusOptions} bind:value={statusFilter} ariaLabel="Ticket status filter" />
              </div>
              <div class="chip-block">
                <span>Extras</span>
                <ChipGroup options={extrasOptions} bind:value={extrasFilter} ariaLabel="Extras filter" />
              </div>
              <div class="chip-block">
                <span>Identity</span>
                <ChipGroup options={identityOptions} bind:value={identityFilter} ariaLabel="Identity filter" />
              </div>
          </div>
          <div class="actions-row">
            <button type="button" on:click={openCreateGuest}>Add guest</button>
          </div>
        </div>
      </SurfaceCard>

	      {#if selectionActive}
	          <SurfaceCard>
	            <div class="selection-bar">
	              <div><strong>{selectedCount}</strong> selected</div>
	              <div class="selection-actions">
	                <button type="button" on:click={selectVisible}>Select visible</button>
	                <button type="button" class="ghost" on:click={clearSelection}>Clear</button>
	                <button type="button" disabled>Mark redeemed</button>
	                <button
	                  type="button"
	                  class="ghost"
	                  on:click={exportSelectedCsv}
	                  disabled={selectedVisibleEntries.length === 0}
	                >
	                  Export CSV
	                </button>
	              </div>
	            </div>
	            <p class="helper-text">Mark redeemed will unlock in the next iteration.</p>
	          </SurfaceCard>
	        {/if}

        <SurfaceCard>
          <table class="guest-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select visible guests"
                    checked={visibleGuestIds.size > 0 && selectedCount === visibleGuestIds.size}
                    on:change={() => {
                      if (selectedCount === visibleGuestIds.size) clearSelection();
                      else selectVisible();
                    }}
                  />
                </th>
                <th class="sortable" on:click={() => toggleSort('name')}>
                  Guest
                  {#if sortKey === 'name'}
                    <span class="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  {/if}
                </th>
                <th>Ticket</th>
                <th>Identity</th>
                <th class="sortable" on:click={() => toggleSort('activity')}>
                  Activity
                  {#if sortKey === 'activity'}
                    <span class="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  {/if}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each sortedGuests as entry (entry.ticket.id)}
                <tr>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTicketIds.has(entry.ticket.id)}
                      on:change={() => toggleSelect(entry.ticket.id)}
                      aria-label={`Select ticket ${entry.ticket.code}`}
                    />
                  </td>
                  <td class="person-col">
                    <div class="name">{entry.person?.name ?? 'Guest'}</div>
                    {#if entry.person?.email}
                      <div class="muted">{entry.person.email}</div>
                    {/if}
                    {#if entry.replacedGuest}
                      <div class="original-note">
                        <span>Originally reserved for {entry.replacedGuest.name ?? 'Guest'}</span>
                        {#if entry.replacedGuest.email}
                          <span>· {entry.replacedGuest.email}</span>
                        {/if}
                        {#if entry.replacedGuest.capturedAt}
                          <span>· noted {formatDateTime(entry.replacedGuest.capturedAt)}</span>
                        {/if}
                      </div>
                    {/if}
                  </td>
                  <td>
                    <div class="code">{entry.ticket.code}</div>
                    <StatusBadge
                      status={normalizeStatus(entry.ticket.status)}
                      label={statusLabels[entry.ticket.status ?? ''] ?? entry.ticket.status ?? 'Issued'}
                    />
                    {#if entry.ticket.redeemedAt}
                      <div class="muted">Redeemed {formatDateTime(entry.ticket.redeemedAt)}</div>
                    {/if}
                  </td>
                  <td>
                    <IdentityBadge status={entry.identityStatus} />
                    {#if entry.person?.externalId}
                      <div class="muted">Clerk linked</div>
                    {:else if entry.person?.email}
                      <div class="muted">Email captured</div>
                    {:else}
                      <div class="muted warning">Needs enrichment</div>
                    {/if}
                  </td>
                  <td>{formatDateTime(entry.lastActivity)}</td>
                  <td class="actions">
                    <button type="button" class="ghost" on:click={() => toggleExpand(entry.ticket.id)}>
                      {expandedTicketId === entry.ticket.id ? 'Hide' : 'View'}
                    </button>
                    <button type="button" class="ghost" on:click={() => openEditGuest(entry)}>Edit</button>
                    <button type="button" class="ghost danger" on:click={() => requestDelete(entry)} disabled={deleteBusy}>
                      Delete
                    </button>
                  </td>
                </tr>
                {#if expandedTicketId === entry.ticket.id}
                  <tr class="expanded">
                    <td></td>
                    <td colspan="5">
                      <div class="expanded-grid">
                        <div>
                          <h4>Extras</h4>
                          {#if entry.ticket.extras?.length}
                            <ul>
                              {#each extrasDisplay(entry) as extraLine}
                                <li>{extraLine}</li>
                              {/each}
                            </ul>
                          {:else}
                            <p class="muted">No extras purchased.</p>
                          {/if}
                        </div>
                        <div>
                          <h4>Contact</h4>
                          <ul>
                            <li>Email: {entry.person?.email ?? '—'}</li>
                            <li>Phone: {entry.person?.phone ?? '—'}</li>
                          </ul>
                        </div>
                        {#if eventQuestion && entry.ticket.preCheckoutAnswer}
                          <div class="question-summary">
                            <h4>Ticket question</h4>
                            <p class="question-summary__prompt">{eventQuestion}</p>
                            <p class="question-summary__answer">{entry.ticket.preCheckoutAnswer}</p>
                          </div>
                        {/if}
                      </div>
                    </td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        </SurfaceCard>
      </div>

      <div class="side-column">
        <SurfaceCard>
          <h3>Event schedule</h3>
          <p>{formatScheduleWindow()}</p>
          {#if data.event}
            <div class="muted">{data.event.location.name}</div>
            {#if data.event.location.address}
              <div class="muted">{data.event.location.address}</div>
            {/if}
          {/if}
        </SurfaceCard>

        <SurfaceCard>
          <h3>Extras availability</h3>
          {#if data.extrasInventory.length === 0}
            <p class="muted">No extras configured.</p>
          {:else}
            <ul class="inventory-list">
              {#each data.extrasInventory as item}
                <li class:low={item.available <= 5 && !item.isUnlimited}>
                  <span>{item.name}</span>
                  {#if item.isUnlimited}
                    <span>Unlimited</span>
                  {:else}
                    <span>{item.available} left · sold {item.sold}</span>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </SurfaceCard>

        <SurfaceCard>
          <h3>Shortcuts</h3>
          {#if importFeedback}
            <p class="success">{importFeedback}</p>
          {:else}
            <p class="muted">Bulk import names to quickly seed this guestlist.</p>
          {/if}
          <div class="rail-actions">
            <button type="button" on:click={openImportModal} disabled={!activeEventId}>Open import</button>
            <button type="button" disabled>View action log</button>
          </div>
        </SurfaceCard>
      </div>
    </div>
  {/if}

  <GuestEditorModal
    open={showGuestModal}
    eventId={data?.event?.id ?? eventId}
    eventName={data?.event?.name ?? null}
    initial={editingGuest}
    people={people}
    existingPersonIds={existingPersonIds}
    on:close={closeGuestModal}
    on:saved={handleGuestSaved}
  />

  <ConfirmDialog
    open={showDeleteModal}
    title="Delete guest"
    message={deleteTarget ? `Remove ${deleteTarget.person?.name ?? 'this guest'} (ticket ${deleteTarget.ticket.code}) from the guestlist? This action cannot be undone.` : ''}
    confirmLabel="Delete guest"
    busy={deleteBusy}
    error={deleteError}
    on:close={closeDeleteModal}
    on:confirm={confirmDelete}
  />

  <GuestImportModal
    open={showImportModal}
    eventId={activeEventId}
    eventName={data?.event?.name ?? null}
    eventCode={data?.event?.id ?? null}
    existingGuestKeys={existingGuestNameKeys}
    on:close={closeImportModal}
    on:imported={handleImportComplete}
  />
</section>

<style>
  .guestlist {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .guestlist-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .breadcrumbs {
    display: flex;
    gap: var(--space-3);
    align-items: flex-start;
  }

  .breadcrumbs h2 {
    margin: 0;
    font-size: 1.55rem;
    font-weight: 600;
  }

  .breadcrumbs p {
    margin: 0.2rem 0 0;
    color: var(--color-text-muted);
  }

  .header-stats {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: center;
  }

  .chip {
    padding: 0.25rem 0.7rem;
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.08);
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .generated {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .guestlist-grid {
    display: grid;
    gap: var(--space-5);
    grid-template-columns: minmax(0, 1fr) 300px;
  }

  .main-column {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .filter-stack {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .actions-row {
    display: flex;
    justify-content: flex-end;
  }

  .actions-row button {
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.55rem 1rem;
    cursor: pointer;
    font-weight: 600;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .empty-state p {
    margin: 0;
  }

  .empty-state button {
    align-self: flex-start;
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.55rem 1rem;
    cursor: pointer;
    font-weight: 600;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  .search input {
    width: 100%;
    padding: 0.65rem 0.9rem;
    border-radius: var(--radius-lg);
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .chip-row {
    display: grid;
    gap: var(--space-3);
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .chip-block {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .selection-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  .selection-actions {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .selection-actions button {
    border: none;
    border-radius: var(--radius-md);
    padding: 0.45rem 0.85rem;
    cursor: pointer;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.32), rgba(178, 127, 244, 0.24));
    color: #050608;
    font-weight: 600;
  }

  .selection-actions button.ghost {
    background: rgba(255, 255, 255, 0.12);
    color: var(--color-text-primary);
  }

  .selection-actions button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .helper-text {
    margin-top: 0.75rem;
    color: var(--color-text-muted);
    font-size: 0.8rem;
  }

  .guest-table {
    width: 100%;
    border-collapse: collapse;
  }

  .guest-table thead {
    background: rgba(255, 255, 255, 0.04);
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .guest-table th,
  .guest-table td {
    padding: 0.75rem 1rem;
    vertical-align: top;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .guest-table th.sortable {
    cursor: pointer;
  }

  .guest-table tbody tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .person-col .name {
    font-weight: 600;
  }

  .person-col .original-note {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .code {
    font-family: var(--font-mono, 'DM Mono', monospace);
    letter-spacing: 0.08em;
  }

  .actions .ghost {
    border: none;
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
    padding: 0.4rem 0.8rem;
    border-radius: var(--radius-md);
    cursor: pointer;
  }

  .actions .danger {
    color: #ff9a9a;
  }

  .actions {
    display: flex;
    gap: 0.4rem;
  }

  .expanded {
    background: rgba(255, 255, 255, 0.03);
  }

  .expanded-grid {
    display: grid;
    gap: var(--space-4);
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .expanded-grid h4 {
    margin: 0 0 0.4rem;
    font-size: 0.9rem;
  }

  .question-summary__prompt {
    margin: 0 0 0.4rem;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.65);
    white-space: pre-line;
  }

  .question-summary__answer {
    margin: 0;
    white-space: pre-line;
    line-height: 1.5;
  }

  .inventory-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.4rem;
  }

  .inventory-list li {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    padding-bottom: 0.35rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .inventory-list li:last-child {
    border-bottom: none;
  }

  .inventory-list li.low span:last-child {
    color: #ffce73;
  }

  .rail-actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .success {
    margin: 0;
    color: #7dffba;
    font-size: 0.85rem;
  }

  .rail-actions button {
    border: none;
    border-radius: var(--radius-md);
    padding: 0.55rem 0.8rem;
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
    cursor: not-allowed;
  }

  .muted {
    color: var(--color-text-muted);
    font-size: 0.85rem;
  }

  .muted.warning {
    color: #ffce73;
  }

  .error-text {
    color: #ff9a9a;
  }

  @media (max-width: 1100px) {
    .guestlist-grid {
      grid-template-columns: 1fr;
    }

    .side-column {
      display: grid;
      gap: var(--space-3);
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }
  }

  @media (max-width: 720px) {
    .guestlist-header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-3);
    }

    .breadcrumbs {
      flex-direction: column;
      gap: var(--space-2);
    }

    .guest-table thead {
      display: none;
    }

    .guest-table tbody tr,
    .guest-table tbody tr.expanded {
      display: block;
      padding: var(--space-3);
    }

    .guest-table td,
    .guest-table th {
      padding: 0 0 var(--space-2) 0;
      border: none;
    }
  }
</style>
